import { useForm, FormProvider } from 'react-hook-form';
import TextInputForm from '@repo/ui/components/forms/TextInputForm';
import UnderlinePlusHeading from '@repo/ui/components/heading/UnderlinePlusHeading';
import { useMemo, useState } from 'react';
import {
    AddMessageFormValues,
    AddMessageSchema,
} from '@/features/services/components/forms/schemas/add-message-schema';
import { apiPost } from '@repo/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import { zodResolver } from '@hookform/resolvers/zod';
import Submission from './Submission';
import PlusIcon from '@repo/ui/components/icons/popups/PlusIcon';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { DateInputForm } from '@repo/ui/components/forms/DateInputForm';
import MultiSelectInputForm from '@repo/ui/components/forms/MultiSelectInputForm';
import type { ServiceModalProps } from '../../types/component-props.types';
// import SelectInputForm from '@repo/ui/components/forms/SelectInputForm';

const COURSE_DAYS_OPTIONS = [
    { name: 'السبت', value: 'saturday' },
    { name: 'الأحد', value: 'sunday' },
    { name: 'الاثنين', value: 'monday' },
    { name: 'الثلاثاء', value: 'tuesday' },
    { name: 'الأربعاء', value: 'wednesday' },
    { name: 'الخميس', value: 'thursday' },
    { name: 'الجمعة', value: 'friday' },
];



export default function ServiceRequestModal({ isOpen, onClose }: ServiceModalProps) {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    useBodyScrollLock(isOpen);

    const defaultValues = useMemo(
        () => ({
            name_ar: '', // Added to match form fields
            name_en: '',
            price: '',
            is_active: true,
            start_date: undefined,
            end_date: undefined,
            // lecturer_name: '',
            number_of_sessions: '',
            days: [] as string[],
        }),
        [],
    );

    const form = useForm<AddMessageFormValues>({
        resolver: zodResolver(AddMessageSchema),
        defaultValues,
    });

    const onSubmit = async (data: AddMessageFormValues) => {
        setError(null);
        setLoading(true);
        try {
            const formData = new FormData();
            Object.entries({ ...data, is_active: data.is_active ? 1 : 0 }).forEach(([key, value]) => {
                if (key === 'days' && Array.isArray(value)) {
                    value.forEach((day, index) => {
                        formData.append(`days[${index}]`, String(day));
                    });
                } else if (value instanceof Date) {
                    formData.append(key, value.toISOString().split('T')[0]);
                } else if (value !== undefined && value !== null && value !== '') {
                    formData.append(key, String(value));
                }
            });

            await apiPost({
                endpoint: endpoints.courses.create,
                body: formData as any,
                config: { headers: { 'Content-Type': 'multipart/form-data' } },
            });
            console.log('dataCourse', data);
            setShowSuccessPopup(true);
        } catch (error: any) {
            if (error.response?.data?.errors) {
                Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    form.setError(field as any, {
                        type: 'manual',
                        message: (messages as string[]).join(', '),
                    });
                });
            } else {
                let errorMessage = 'حدث خطأ أثناء إرسال البيانات. يرجى المحاولة مرة أخرى.';
                if (error.response) {
                    errorMessage = error.response.data.msg || errorMessage;
                } else if (error.request) {
                    errorMessage = 'تعذر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.';
                }
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // const [dataEmployee, setDataEmployee] = useState<any[]>([]);

    // const fetchEmployee = useCallback(async () => {
    //     const response = await apiGet({
    //         endpoint: endpoints.employee.select,
    //         queryParams: {
    //             page: 1,

    //         },
    //     });
    //     const employees = response.data.data?.data || response.data.data || [];
    //     // console.log('employees', employees);
    //     setDataEmployee(employees);
    // }, []);

    // useEffect(() => {
    //     fetchEmployee();
    // }, [fetchEmployee]);

    // const mappedEmployees = dataEmployee.map((employee: any) => ({
    //     name: employee.name,
    //     value: employee.id,
    // }));

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative max-h-[90vh] overflow-y-auto bg-white-blue-100 rounded-[12px] md:p-8 p-6 w-full md:max-w-xl max-w-md shadow-lg mx-3">
                <FormProvider {...form}>
                    <UnderlinePlusHeading text="اضافة دوره جديدة" onClick={onClose} icon={PlusIcon} />
                    <div className="flex flex-col md:gap-4 sm:gap-[14px] gap-3">
                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <TextInputForm
                                className="w-full"
                                name="name_ar"
                                label="اسم الدوره بالعربية"
                                placeholder="ادخل اسم القسم بالعربي"
                                disabled={loading}
                            />
                            <TextInputForm
                                className="w-full"
                                name="name_en"
                                label="اسم الدوره بالانجليزية"
                                placeholder="ادخل اسم القسم بالانجليزي"
                                disabled={loading}
                            />
                        </div>
                        {/* 
                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <SelectInputForm
                                className="w-full"
                                name="lecturer_name"
                                label="اسم المحاضر"
                                placeholder="اسم المحاضر"
                                disabled={loading}
                                options={mappedEmployees}
                            />
                        </div> */}

                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <TextInputForm
                                className="w-full"
                                name="number_of_sessions"
                                label="عدد الحصص"
                                placeholder="عدد الحصص"
                                disabled={loading}
                            />
                            <TextInputForm
                                className="w-full"
                                name="price"
                                label="السعر"
                                placeholder="السعر"
                                disabled={loading}
                            />
                        </div>

                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <DateInputForm
                                className="w-full"
                                name="start_date"
                                label="تاريخ البداية"
                                placeholder="تاريخ البداية"
                            />
                            <DateInputForm
                                className="w-full"
                                name="end_date"
                                label="تاريخ النهاية"
                                placeholder="تاريخ النهاية"
                            />
                        </div>

                        <div className="flex sm:flex-row flex-col lg:gap-6 gap-4">
                            <MultiSelectInputForm
                                className="w-full"
                                name="days"
                                label="أيام الدورة"
                                placeholder="اختر أيام الدورة"
                                options={COURSE_DAYS_OPTIONS}
                                disabled={loading}
                            />
                        </div>
                        {/* <div className="mb-6">
                                <TextAreaInputForm
                                    name="notes"
                                    label="ملاحظات"
                                    placeholder="ملاحظات بسيطة"
                                    className="!h-32"
                                    disabled={loading}
                                />
                            </div> */}
                    </div>

                    {error && <p className="text-red-600 mt-4">{error}</p>}
                    <Submission
                        // maintenanceOrder={null}
                        loading={loading}
                        setShowCancelPopup={setShowCancelPopup}
                        setShowSuccessPopup={setShowSuccessPopup}
                        showCancelPopup={showCancelPopup}
                        showSuccessPopup={showSuccessPopup}
                        onClose={onClose}
                        onAddAnother={() => {
                            form.reset(); // مسح البيانات
                            setShowSuccessPopup(false); // إغلاق البوب أب
                        }}
                        onSubmit={form.handleSubmit(onSubmit)}
                    />
                </FormProvider>
            </div>
        </div>
    );
}
